import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { type ApiResult, Transfers } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import {
    type TransferData,
    type TransferId,
    type TransfersResponse,
    type UserPendingIncoming,
} from '@app/client/common/structs/emoji/transfers';

import { type RootState } from '../reducers';
import {
    ACTION_SET_INCOMING_TRANSFERS,
    ACTION_SET_OUTGOING_TRANSFERS,
    ACTION_SET_PENDING_INCOMING,
    ACTION_SET_TRANSFER_DATA,
    ACTION_SET_TRANSFER_PANEL_OPEN,
    type TransfersActionTypes,
} from '../types/transfers';

import { setError } from './error';
import { getCurrentUserEmojis } from './user-emojis';

export const setIncomingTransfers = (incomingTransfers: TransfersResponse[]): TransfersActionTypes => ({
    type: ACTION_SET_INCOMING_TRANSFERS,
    incomingTransfers,
});

export const setOutgoingTransfers = (outgoingTransfers: TransfersResponse[]): TransfersActionTypes => ({
    type: ACTION_SET_OUTGOING_TRANSFERS,
    outgoingTransfers,
});

export const setTransferPanelOpen = (transferPanelOpen: boolean): TransfersActionTypes => ({
    type: ACTION_SET_TRANSFER_PANEL_OPEN,
    transferPanelOpen,
});

export const setUserPendingIncoming = (userPendingIncoming: UserPendingIncoming): TransfersActionTypes => ({
    type: ACTION_SET_PENDING_INCOMING,
    userPendingIncoming,
});

export const setTransferData = (emojiId: EmojiId, transferData: TransferData): TransfersActionTypes => ({
    type: ACTION_SET_TRANSFER_DATA,
    emojiId,
    transferData,
});

export function getOutgoingTransfers() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        //TODO Fix pagination
        const r = await apiFactory().call(Transfers.getOutgoingCollection, { query: { limit: 500 } });
        try {
            if (r.ok) {
                dispatch(setOutgoingTransfers(r.value.data));
            } else {
                dispatch(setError({ error: r, notify: true }));
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            dispatch(setError({ ...e, notify: true }));
        }
    };
}

export function getIncomingTransfers() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        const r = await apiFactory().call(Transfers.getIncomingCollection, { query: { limit: 500 } });
        try {
            if (r.ok) {
                dispatch(setIncomingTransfers(r.value));
            } else {
                dispatch(setError({ error: r, notify: true }));
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            dispatch(setError({ ...e, notify: true }));
        }
    };
}

export function cancelTransfer(id: TransferId) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<TransfersResponse>> {
        return apiFactory()
            .call(Transfers.remove, { id })
            .then((r) => {
                if (r.ok) {
                    dispatch(getOutgoingTransfers());
                    dispatch(getCurrentUserEmojis());
                }
                return r;
            })
            .catch((e) => {
                dispatch(setError({ ...e, notify: true }));
                return e;
            })
            .finally(() => {
                dispatch(setTransferPanelOpen(false));
            });
    };
}
